import { useState, useEffect, useCallback } from "react";
import { Category, Product, StaffMember } from "../types/product";
import api from "../../../../../../features/auth/axiosConfig";
import { notifyError, notifySuccess } from "src/assets/toastService";

interface UseEditProductProps {
  product: Product;
  onClose: () => void;
  onUpdate: () => void;
  open: boolean;
}

export const useEditProduct = ({ product, onClose, onUpdate, open }: UseEditProductProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [formData, setFormData] = useState({
    productName: product.ProductName || "",
    arabicName: product.ArabicName || "",
    amount: product.Amount || "",
    categoryId: product.category_id || "",
    staffIds: [] as string[],
    type: product.Type || "",
    image: null as File | null,
  });
  const [previewImage, setPreviewImage] = useState<string | null>(product.image_url || null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const getCategoryInitials = useCallback((categoryName: string): string => {
    const words = categoryName.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 5).toUpperCase();
    } else {
      return words.map((word) => word[0].toUpperCase()).join("");
    }
  }, []);

  const updateProductNameWithCategory = useCallback(
  (productName: string, categoryId: string): string => {
    if (!categoryId || !categories.length) return productName;

    const selectedCategory = categories.find(
      (cat) => cat._id.$oid === categoryId
    );
    if (!selectedCategory) return productName;

    const categoryInitials = getCategoryInitials(
      selectedCategory.CategoryName
    );

    const cleanedName = productName
      .replace(/\s*\[[^\]]+\]/g, "")
      .trim();

    return `${cleanedName} [${categoryInitials}]`;
  },
  [categories, getCategoryInitials]
);


  const getSelectedStaffNames = useCallback(() => {
    return staffList.filter((staff) => formData.staffIds.includes(staff._id.$oid)).map((staff) => staff.staff_name);
  }, [formData.staffIds, staffList]);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      api
        .get(`/app/v1/pos/category?include_deleted=False&pageNumber=&pageSize=`)
        .then((res) => {
          setCategories(res.data.category_details);
        })
        .catch(console.error);

      api
        .get(`/app/v1/pos/staff?is_deleted=false`)
        .then((res) => {
          const staffData: StaffMember[] = res.data?.result || [];
          setStaffList(staffData);

          const currentStaffNames = product.staff_list?.map((staff) => staff.name) || [];
          const selectedIds = staffData.filter((staff) => currentStaffNames.includes(staff.staff_name)).map((staff) => staff._id.$oid);

          setFormData((prev) => ({ ...prev, staffIds: selectedIds }));
        })
        .catch(console.error)
        .finally(() => setIsLoading(false));
    }
  }, [product, open]);

  useEffect(() => {
    const currentStaffNames = product.staff_list?.map((staff) => staff.name) || [];
    const currentStaffIds = staffList.filter((staff) => currentStaffNames.includes(staff.staff_name)).map((staff) => staff._id.$oid);

    const hasFormChanges =
      formData.productName.trim() !== product.ProductName.trim() ||
      formData.arabicName.trim() !== product.ArabicName.trim() ||
      formData.amount.toString() !== product.Amount.toString() ||
      formData.categoryId !== product.category_id ||
      formData.type !== (product.Type || "") ||
      formData.image !== null || // image changed
      JSON.stringify(formData.staffIds.sort()) !== JSON.stringify(currentStaffIds.sort()); // staff order-insensitive

    setHasChanges(hasFormChanges);

     // ✅ Amount validation like AddProductModal
  const amountValue = parseFloat(formData.amount);
  const isValid =
    formData.productName.trim() !== "" &&
    formData.arabicName.trim() !== "" &&
    formData.categoryId.trim() !== "" &&
    !isNaN(amountValue) &&
    amountValue > 0;

  setIsFormValid(isValid);
  }, [formData, product, staffList]);

  const handleInputChange = useCallback(
    (field: string, value: string | string[] | File | null) => {
      setFormData((prev) => {
        const newFormData = { ...prev, [field]: value };

        if (field === "categoryId" && typeof value === "string" && value) {
          const updatedProductName = updateProductNameWithCategory(prev.productName, value);
          newFormData.productName = updatedProductName;
        }

        return newFormData;
      });
    },
    [updateProductNameWithCategory]
  );

  const handleFileChange = useCallback(
    (file: File | null) => {
      if (file) {
        const reader = new FileReader();
        reader.onload = () => setPreviewImage(reader.result as string);
        reader.readAsDataURL(file);
        handleInputChange("image", file);
      }
    },
    [handleInputChange]
  );

  const handleUpdate = useCallback(async () => {
    if (!isFormValid) return;

    setIsLoading(true);

    // Get staff names from selected IDs for API submission
    const selectedStaffNames = staffList.filter((staff) => formData.staffIds.includes(staff._id.$oid)).map((staff) => staff.staff_name);

    const updateFormData = new FormData();
    updateFormData.append("ProductName", formData.productName);
    updateFormData.append("Amount", formData.amount);
    updateFormData.append("ArabicName", formData.arabicName);
    updateFormData.append("category_id", formData.categoryId);

    if (formData.type) {
      updateFormData.append("Type", formData.type);
    }

    updateFormData.append("staff_list", JSON.stringify(selectedStaffNames));

    if (formData.image) {
      updateFormData.append("image_url", formData.image);
    }

    console.log("........................", updateFormData.getAll("image"));

    try {
      await api.put(`/app/v1/pos/product/${product._id.$oid}`, updateFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      notifySuccess("Product updated successfully");
      onClose();
      setTimeout(() => {
        onUpdate();
      }, 2000);
    } catch (error: any) {
      console.error("Error updating product:", error);
      let errorMessage = "Failed to update product";

      if (error.response) {
        const backendError = error.response.data;
        if (backendError.message) {
          errorMessage = backendError.message;
        } else if (backendError.error) {
          errorMessage = backendError.error;
        } else if (typeof backendError === "string") {
          errorMessage = backendError;
        } else {
          errorMessage = "An error occurred while updating the product";
        }
      } else if (error.request) {
        errorMessage = "No response from server. Please check your connection.";
      } else {
        errorMessage = error.message || "An unexpected error occurred";
      }
      notifyError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [formData, isFormValid, staffList, product, onUpdate, onClose]);

  const removeEmojis = useCallback((str: string): string => {
    return str.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\u24C2|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|\uD83E[\uDD00-\uDFFF])/g, "");
  }, []);

  const formatAmount = useCallback((value: string): string => {
    if (!value) return "0.00";
    let [integerPart, decimalPart] = value.split(".");
    if (!integerPart) integerPart = "0";
    if (!decimalPart) decimalPart = "00";
    else if (decimalPart.length === 1) decimalPart = decimalPart + "0";
    else if (decimalPart.length > 2) decimalPart = decimalPart.slice(0, 2);
    return `${integerPart}.${decimalPart}`;
  }, []);

  return {
    isLoading,
    categories,
    staffList,
    formData,
    previewImage,
    hasChanges,
    isFormValid,
    handleInputChange,
    handleFileChange,
    handleUpdate,
    removeEmojis,
    formatAmount,
    getCategoryInitials,
    updateProductNameWithCategory,
    getSelectedStaffNames,
  };
};
